// 119 — Transferencia (Fase 7, Tanda 13, ver PLAN.md). Transcripción campo por campo del PDF oficial del Anexo 1 MSP:
// hojas idx 251-252 (footer `SNS-MSP/HCU-form.119/2021`; la hoja 1 lo imprime con punto por error del papel).
// Informe de brechas en `docs/hce-fase7/brechas-117-118-119-120-122.md`.
//
// Retirado de la UI por no estar impreso: "Acompañante", y la toma única de signos vitales (traía peso y talla y no
// tenía tensión arterial ni glucosa; el papel trae 5 tomas): ya no se crean Observations, la grilla F va como `Par[]`
// en el PDF firmado. Decisiones ante lo que el papel no resuelve: el bloque A del papel son los datos de la
// ambulancia, no del establecimiento, así que se conserva el encabezado MSP estándar para identificar al paciente y
// el bloque A se captura aparte; las dos filas de equipos sin rótulo no se implementan; las tablas J/K/M/H/F usan
// filas fijas (H 12, J 4, K 2 prefijadas, M 5, F 5) con texto libre; el examen pupilar va con casillas por toma (reactividad OD/OI
// Sí/No, Isocoria o Anisocoria, Miosis o Midriasis); la condición de la edad ofrece solo Horas/Días/Meses/Años (el instructivo, bloque B,
// no lista "Ignorado" aunque la hoja imprima esa celda: no hay regla que la defina); Institución del sistema (A, origen y destino) es el
// catálogo del instructivo MSP/IESS/ISSFA/ISPOL; el
// kilometraje es texto libre (sin unidad impresa); las firmas de terceros son manuscritas (se capturan nombre y
// cargo). M y N (transporte aéreo) son opcionales.
//
// FHIR: se conserva el `Procedure` `completed` con `code.text` "Transferencia", `note` (origen/destino), `reasonCode`
// (motivo) y `reasonReference` de la condición.
import { conCodigoCie, paresDeCampos, valoresVacios, type CampoSimple, type OpcionCampo, type Valores } from './camposSimplesDatos';
import type { DiagnosticoCie10Valor } from './SelectorDiagnosticoCie10';
import { RANGOS_GLASGOW, totalGlasgow } from './calculosClinicosDatos';
import { estadoInicialTablaConColumnas, resumenFilaTablaConColumnas, type ColumnaTablaConColumnas, type EstadoTablaConColumnas, type FilaTablaConColumnas } from './uci/TablaConColumnas';
import type { TomaSignos } from './signosVitalesToma';
import type { Par } from './index';

const casilla = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta, tipo: 'casilla' });
const area = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta, tipo: 'area' });
const opciones = (...e: readonly string[]): readonly OpcionCampo[] => e.map((x) => ({ valor: x, etiqueta: x }));
const filas = (n: number): readonly FilaTablaConColumnas[] => Array.from({ length: n }, (_, i) => ({ clave: `f${i + 1}`, etiqueta: String(i + 1) }));

/** Catálogo del instructivo (bloque A): "MSP, IESS, ISSFFA, ISPOL" (el papel imprime ISSFFA; la institución es ISSFA). */
export const INSTITUCIONES_SISTEMA_119: readonly OpcionCampo[] = opciones('MSP', 'IESS', 'ISSFA', 'ISPOL');

export const CAMPOS_AMBULANCIA_119: readonly CampoSimple[] = [
  { clave: 'entidad', etiqueta: 'Entidad del sistema', tipo: 'select', opciones: INSTITUCIONES_SISTEMA_119 },
  { clave: 'baseFisica', etiqueta: 'Base física' },
  { clave: 'unicodigo', etiqueta: 'Unicódigo' },
  { clave: 'numeroAmbulancia', etiqueta: 'N° ambulancia' },
  { clave: 'tipologia', etiqueta: 'Tipología' },
  { clave: 'zona', etiqueta: 'Zona' },
];

export const CAMPOS_GENERALES_119: readonly CampoSimple[] = [
  casilla('trauma', 'Tipo de paciente — trauma'),
  casilla('gineco', 'Tipo de paciente — gineco-obstétrica'),
  casilla('clinica', 'Tipo de paciente — clínica'),
  casilla('saludMental', 'Tipo de paciente — salud mental'),
  { clave: 'condicionEdad', etiqueta: 'Condición de la edad', tipo: 'select', opciones: opciones('Horas', 'Días', 'Meses', 'Años') },
  area('condicionPaciente', 'Condición del paciente (descripción de la condición de salud)'),
];

export const NUM_DIAGNOSTICOS_119 = 2;

export const CAMPOS_TRANSFERENCIA_119: readonly CampoSimple[] = [
  { clave: 'fecha', etiqueta: 'Fecha', tipo: 'fecha' },
  { clave: 'hora', etiqueta: 'Hora', tipo: 'hora' },
  casilla('motivoExamenes', 'Motivo — exámenes complementarios'),
  casilla('motivoReferencia', 'Motivo — referencia'),
  casilla('motivoDerivacion', 'Motivo — derivación'),
  casilla('motivoContrarreferencia', 'Motivo — contrarreferencia'),
  casilla('motivoReferenciaInversa', 'Motivo — referencia inversa'),
  casilla('motivoAlta', 'Motivo — alta'),
  casilla('medioTerrestre', 'Medio de transporte — terrestre'),
  casilla('medioAereo', 'Medio de transporte — aéreo'),
  casilla('medioFluvial', 'Medio de transporte — fluvial'),
  casilla('medioMaritimo', 'Medio de transporte — marítimo'),
];

const establecimiento = (p: string, rotulo: string): readonly CampoSimple[] => [
  { clave: `${p}Institucion`, etiqueta: `${rotulo} — institución del sistema`, tipo: 'select', opciones: INSTITUCIONES_SISTEMA_119 },
  { clave: `${p}Unicodigo`, etiqueta: `${rotulo} — unicódigo` },
  { clave: `${p}Nombre`, etiqueta: `${rotulo} — nombre del establecimiento de salud` },
  { clave: `${p}Distrito`, etiqueta: `${rotulo} — distrito` },
  { clave: `${p}Provincia`, etiqueta: `${rotulo} — provincia` },
];
export const CAMPOS_ORIGEN_119: readonly CampoSimple[] = [
  ...establecimiento('origen', 'Origen'),
  { clave: 'origenProfesional', etiqueta: 'Origen — profesional de salud solicitante' },
  { clave: 'origenCargo', etiqueta: 'Origen — cargo' },
];
export const CAMPOS_DESTINO_119: readonly CampoSimple[] = [
  ...establecimiento('destino', 'Destino'),
  { clave: 'destinoProfesional', etiqueta: 'Destino — profesional de salud que recibe' },
  { clave: 'destinoCargo', etiqueta: 'Destino — cargo' },
];

const equipo = (p: string, rotulo: string): readonly CampoSimple[] => [
  { clave: `${p}Cantidad`, etiqueta: `${rotulo} — cantidad`, tipo: 'numero' },
  { clave: `${p}Marca`, etiqueta: `${rotulo} — marca` },
];
export const CAMPOS_EQUIPOS_119: readonly CampoSimple[] = [
  ...equipo('bomba', 'Bomba de infusión'),
  ...equipo('monitor', 'Monitor portátil'),
  ...equipo('ventilador', 'Ventilador mecánico'),
  ...equipo('termocuna', 'Termocuna'),
  { clave: 'entregaNombre', etiqueta: 'Persona que entrega — nombre' },
  { clave: 'entregaCargo', etiqueta: 'Persona que entrega — cargo' },
  { clave: 'recibeNombre', etiqueta: 'Persona que recibe — nombre' },
  { clave: 'recibeCargo', etiqueta: 'Persona que recibe — cargo' },
];

export const FILAS_CONSTANTES_119 = filas(5);
export const COLUMNAS_CONSTANTES_119: readonly ColumnaTablaConColumnas[] = [
  { clave: 'hora', etiqueta: 'Hora' },
  { clave: 'fc', etiqueta: 'FC/min' },
  { clave: 'temperatura', etiqueta: 'Temperatura °C' },
  { clave: 'ta', etiqueta: 'Tensión arterial (mm/Hg)' },
  { clave: 'fr', etiqueta: 'FR/min' },
  { clave: 'spo2', etiqueta: 'Pulsioximetría (%)' },
  { clave: 'glucosa', etiqueta: 'Glucosa mg/dl' },
  // Glasgow: cada componente se valida contra su rango (ocular 1-4, verbal 1-5, motor 1-6; fuera de rango se marca sin bloquear el guardado) y el
  // total (3-15) se calcula de los tres — solo lectura; vacío si falta alguno o alguno es inválido.
  { clave: 'ocular', etiqueta: 'Glasgow ocular', rango: RANGOS_GLASGOW.ocular },
  { clave: 'verbal', etiqueta: 'Glasgow verbal', rango: RANGOS_GLASGOW.verbal },
  { clave: 'motor', etiqueta: 'Glasgow motor', rango: RANGOS_GLASGOW.motor },
  { clave: 'totalGlasgow', etiqueta: 'Glasgow total (/15)', calculada: (f) => String(totalGlasgow(f.ocular, f.verbal, f.motor) ?? '') },
  // Examen pupilar: casillas como el papel (reactividad OD y OI Sí/No; Isocoria o Anisocoria; Miosis o Midriasis), sin valor por defecto.
  { clave: 'reactividadOd', etiqueta: 'Pupilas reactividad OD', tipo: 'siNo' },
  { clave: 'reactividadOi', etiqueta: 'Pupilas reactividad OI', tipo: 'siNo' },
  { clave: 'isocoria', etiqueta: 'Isocoria / anisocoria', tipo: 'opciones', opciones: ['Isocoria', 'Anisocoria'] },
  { clave: 'miosis', etiqueta: 'Miosis / midriasis', tipo: 'opciones', opciones: ['Miosis', 'Midriasis'] },
];

export const CAMPOS_PROCEDIMIENTOS_119: readonly CampoSimple[] = [area('procedimientos', 'Procedimientos durante la transferencia')];

export const FILAS_DESCARGO_119 = filas(12);
export const COLUMNAS_DESCARGO_119: readonly ColumnaTablaConColumnas[] = [
  { clave: 'medicamento', etiqueta: 'Medicamento / dispositivo' },
  { clave: 'presentacion', etiqueta: 'Presentación' },
  { clave: 'consumo', etiqueta: 'Consumo' },
];

export const CAMPOS_NOVEDADES_119: readonly CampoSimple[] = [area('novedades', 'Novedades')];

const COLUMNAS_PERSONAL: readonly ColumnaTablaConColumnas[] = [
  { clave: 'cargo', etiqueta: 'Cargo' },
  { clave: 'apellidos', etiqueta: 'Apellidos' },
  { clave: 'nombres', etiqueta: 'Nombres' },
];
export const FILAS_EQUIPO_119 = filas(4);
export const COLUMNAS_EQUIPO_119 = COLUMNAS_PERSONAL;
export const CAMPOS_OBSERVACIONES_EQUIPO_119: readonly CampoSimple[] = [area('observaciones', 'Observaciones (equipo de unidad de transporte)')];

export const FILAS_REGULADOR_119: readonly FilaTablaConColumnas[] = [
  { clave: 'radio', etiqueta: 'Radio despachador' },
  { clave: 'evaluador', etiqueta: 'Evaluador pre-hospitalario' },
];
export const COLUMNAS_REGULADOR_119: readonly ColumnaTablaConColumnas[] = [
  { clave: 'apellidos', etiqueta: 'Apellidos' },
  { clave: 'nombres', etiqueta: 'Nombres' },
];
export const CAMPOS_OBSERVACIONES_REGULADOR_119: readonly CampoSimple[] = [area('observaciones', 'Observaciones (centro regulador)')];

const horaKm = (p: string, rotulo: string): readonly CampoSimple[] => [
  { clave: `${p}Hora`, etiqueta: `${rotulo} — hora`, tipo: 'hora' },
  { clave: `${p}Km`, etiqueta: `${rotulo} — kilometraje` },
];
const soloHora = (p: string, rotulo: string): readonly CampoSimple[] => [{ clave: `${p}Hora`, etiqueta: `${rotulo} — hora`, tipo: 'hora' }];
export const CAMPOS_TRANSPORTE_119: readonly CampoSimple[] = [
  ...soloHora('requerimiento', 'Hora de requerimiento'),
  ...horaKm('salidaBase', 'Salida de base'),
  ...horaKm('llegadaOrigen', 'Llegada al establecimiento'),
  ...soloHora('contacto', 'Contacto con el paciente'),
  ...soloHora('salidaOrigen', 'Salida del establecimiento'),
  ...horaKm('llegadaReceptor', 'Llegada al establecimiento receptor'),
  ...soloHora('entrega', 'Entrega del paciente'),
  ...soloHora('salidaReceptor', 'Salida del establecimiento receptor'),
  ...horaKm('llegadaBase', 'Llegada a base'),
];

export const FILAS_AEREA_119 = filas(5);
export const COLUMNAS_AEREA_119 = COLUMNAS_PERSONAL;
export const CAMPOS_OBSERVACIONES_AEREA_119: readonly CampoSimple[] = [area('observaciones', 'Observaciones (tripulación aérea)')];

export const CAMPOS_AERONAVE_119: readonly CampoSimple[] = [
  { clave: 'institucion', etiqueta: 'Institución', tipo: 'select', opciones: opciones('Fuerzas Armadas', 'Policía Nacional', 'Cuerpo de Bomberos', 'Institución privada') },
  { clave: 'nombreInstitucion', etiqueta: 'Nombre de la institución' },
  { clave: 'tipoAla', etiqueta: 'Tipo de aeronave — ala', tipo: 'select', opciones: opciones('Ala fija', 'Ala rotatoria') },
  { clave: 'presurizacion', etiqueta: 'Tipo de aeronave — presurización', tipo: 'select', opciones: opciones('Presurizada', 'No presurizada') },
  { clave: 'altitud', etiqueta: 'Datos de vuelo — altitud' },
  { clave: 'velocidad', etiqueta: 'Datos de vuelo — velocidad' },
  { clave: 'puertoSalida', etiqueta: 'Puerto / punto de salida' },
  { clave: 'puertoArribo', etiqueta: 'Puerto / punto de arribo' },
  { clave: 'horaSalida', etiqueta: 'Hora de salida', tipo: 'hora' },
  { clave: 'horaLlegada', etiqueta: 'Hora de llegada', tipo: 'hora' },
  { clave: 'recibeAmbulancia', etiqueta: 'Recibe ambulancia terrestre', tipo: 'siNo' },
];

export const CAMPOS_DESTINO_PACIENTE_119: readonly CampoSimple[] = [
  { clave: 'signos', etiqueta: 'Signos vitales', tipo: 'select', opciones: opciones('Presencia de signos vitales', 'Ausencia de signos vitales') },
  { clave: 'establecimiento', etiqueta: 'Nombre del establecimiento de salud que recibe' },
  { clave: 'horaLlegada', etiqueta: 'Hora de llegada al establecimiento de salud', tipo: 'hora' },
  { clave: 'horaFirma', etiqueta: 'Hora de firma', tipo: 'hora' },
  { clave: 'recibeApellidos', etiqueta: 'Quien recibe — apellidos' },
  { clave: 'recibeNombres', etiqueta: 'Quien recibe — nombres' },
  { clave: 'recibeCargo', etiqueta: 'Quien recibe — cargo' },
  { clave: 'entregaApellidos', etiqueta: 'Quien entrega — apellidos' },
  { clave: 'entregaNombres', etiqueta: 'Quien entrega — nombres' },
  { clave: 'entregaCargo', etiqueta: 'Quien entrega — cargo' },
];

export interface Estado119 {
  readonly ambulancia: Valores;
  readonly generales: Valores;
  readonly diagnosticos: readonly DiagnosticoCie10Valor[];
  readonly transferencia: Valores;
  readonly origen: Valores;
  readonly destino: Valores;
  readonly equipos: Valores;
  readonly constantes: EstadoTablaConColumnas;
  readonly procedimientos: Valores;
  readonly descargo: EstadoTablaConColumnas;
  readonly novedades: Valores;
  readonly equipoTransporte: EstadoTablaConColumnas;
  readonly obsEquipo: Valores;
  readonly regulador: EstadoTablaConColumnas;
  readonly obsRegulador: Valores;
  readonly transporte: Valores;
  readonly aerea: EstadoTablaConColumnas;
  readonly obsAerea: Valores;
  readonly aeronave: Valores;
  readonly destinoPaciente: Valores;
}

export function estadoInicial119(): Estado119 {
  return {
    ambulancia: valoresVacios(CAMPOS_AMBULANCIA_119),
    generales: valoresVacios(CAMPOS_GENERALES_119),
    diagnosticos: Array.from({ length: NUM_DIAGNOSTICOS_119 }, () => ({ texto: '', cie10: null })),
    transferencia: valoresVacios(CAMPOS_TRANSFERENCIA_119),
    origen: valoresVacios(CAMPOS_ORIGEN_119),
    destino: valoresVacios(CAMPOS_DESTINO_119),
    equipos: valoresVacios(CAMPOS_EQUIPOS_119),
    constantes: estadoInicialTablaConColumnas(FILAS_CONSTANTES_119, COLUMNAS_CONSTANTES_119),
    procedimientos: valoresVacios(CAMPOS_PROCEDIMIENTOS_119),
    descargo: estadoInicialTablaConColumnas(FILAS_DESCARGO_119, COLUMNAS_DESCARGO_119),
    novedades: valoresVacios(CAMPOS_NOVEDADES_119),
    equipoTransporte: estadoInicialTablaConColumnas(FILAS_EQUIPO_119, COLUMNAS_EQUIPO_119),
    obsEquipo: valoresVacios(CAMPOS_OBSERVACIONES_EQUIPO_119),
    regulador: estadoInicialTablaConColumnas(FILAS_REGULADOR_119, COLUMNAS_REGULADOR_119),
    obsRegulador: valoresVacios(CAMPOS_OBSERVACIONES_REGULADOR_119),
    transporte: valoresVacios(CAMPOS_TRANSPORTE_119),
    aerea: estadoInicialTablaConColumnas(FILAS_AEREA_119, COLUMNAS_AEREA_119),
    obsAerea: valoresVacios(CAMPOS_OBSERVACIONES_AEREA_119),
    aeronave: valoresVacios(CAMPOS_AERONAVE_119),
    destinoPaciente: valoresVacios(CAMPOS_DESTINO_PACIENTE_119),
  };
}

/** Las 5 tomas de F como `TomaSignos`: la hora es la de cada fila y la fecha la de la transferencia (D). */
export function tomasDe119(e: Estado119): TomaSignos[] {
  return FILAS_CONSTANTES_119.map((f) => {
    const c = e.constantes[f.clave] ?? {};
    return { fecha: e.transferencia.fecha, hora: c.hora, fc: c.fc, temperatura: c.temperatura, presion: c.ta, fr: c.fr, spo2: c.spo2 };
  });
}

const MOTIVOS = ['motivoExamenes', 'motivoReferencia', 'motivoDerivacion', 'motivoContrarreferencia', 'motivoReferenciaInversa', 'motivoAlta'] as const;

/** Motivos marcados (etiqueta sin el prefijo "Motivo — ") — va a `Procedure.reasonCode`. */
export function motivosMarcados(t: Valores): string[] {
  return MOTIVOS.filter((c) => t[c] === 'true').map((c) => CAMPOS_TRANSFERENCIA_119.find((x) => x.clave === c)!.etiqueta.replace('Motivo — ', ''));
}

const filasPares = (titulo: string, f: readonly FilaTablaConColumnas[], c: readonly ColumnaTablaConColumnas[], e: EstadoTablaConColumnas): Par[] =>
  f.map((x) => ({ pregunta: `${titulo} — ${x.etiqueta}`, respuesta: resumenFilaTablaConColumnas(e, x.clave, c) }));

export function paresDe119(e: Estado119): Par[] {
  return [
    ...paresDeCampos(CAMPOS_AMBULANCIA_119, e.ambulancia, 'Ambulancia — '),
    ...paresDeCampos(CAMPOS_GENERALES_119, e.generales),
    ...e.diagnosticos.map((d, i) => ({ pregunta: `Diagnóstico ${i + 1}`, respuesta: conCodigoCie(d) })),
    ...paresDeCampos(CAMPOS_TRANSFERENCIA_119, e.transferencia),
    ...paresDeCampos(CAMPOS_ORIGEN_119, e.origen),
    ...paresDeCampos(CAMPOS_DESTINO_119, e.destino),
    ...paresDeCampos(CAMPOS_EQUIPOS_119, e.equipos),
    ...filasPares('Constantes vitales', FILAS_CONSTANTES_119, COLUMNAS_CONSTANTES_119, e.constantes),
    ...paresDeCampos(CAMPOS_PROCEDIMIENTOS_119, e.procedimientos),
    ...filasPares('Descargo de medicamentos y dispositivos', FILAS_DESCARGO_119, COLUMNAS_DESCARGO_119, e.descargo),
    ...paresDeCampos(CAMPOS_NOVEDADES_119, e.novedades),
    ...filasPares('Equipo de unidad de transporte', FILAS_EQUIPO_119, COLUMNAS_EQUIPO_119, e.equipoTransporte),
    ...paresDeCampos(CAMPOS_OBSERVACIONES_EQUIPO_119, e.obsEquipo),
    ...filasPares('Centro regulador', FILAS_REGULADOR_119, COLUMNAS_REGULADOR_119, e.regulador),
    ...paresDeCampos(CAMPOS_OBSERVACIONES_REGULADOR_119, e.obsRegulador),
    ...paresDeCampos(CAMPOS_TRANSPORTE_119, e.transporte, 'Datos de transporte — '),
    ...filasPares('Tripulación aérea', FILAS_AEREA_119, COLUMNAS_AEREA_119, e.aerea),
    ...paresDeCampos(CAMPOS_OBSERVACIONES_AEREA_119, e.obsAerea),
    ...paresDeCampos(CAMPOS_AERONAVE_119, e.aeronave, 'Aeronave — '),
    ...paresDeCampos(CAMPOS_DESTINO_PACIENTE_119, e.destinoPaciente, 'Destino del paciente — '),
  ].filter((p) => p.respuesta.trim() !== '');
}
